import { useEffect, useRef, useState, useMemo } from "react";
import {
  CheckCircle2, ArrowDownAZ, ArrowUp10, ArrowDown10,
} from "lucide-react";
import { ClientCard } from "./ClientCard";

// One Kanban column — sticky header, per-column sort menu (persisted
// to localStorage), drag/drop target for cross-column stage moves.
// Sticky-header CSS is load-bearing (see MigrationBoard comments) —
// do not add overflow-* on the outer flex row.

export function StageColumn({ stage, clients, onOpen, onDropClient, isPartnerView, syncroUrlBase }) {
  const [dragOver, setDragOver] = useState(false);
  const sortStorageKey = `kanban_col_sort_${stage.key}`;
  const [sortMode, setSortMode] = useState(() => {
    try { return localStorage.getItem(sortStorageKey) || "alpha"; }
    catch (_) { return "alpha"; }
  });
  const [menuOpen, setMenuOpen] = useState(false);
  const menuBtnRef = useRef(null);
  const menuRef = useRef(null);
  // Close the sort menu on any click OUTSIDE the button + menu (avoid a
  // full-viewport overlay button so we don't leave an invisible click
  // blocker if state gets weird).
  useEffect(() => {
    if (!menuOpen) return undefined;
    function onDocClick(e) {
      const t = e.target;
      if (menuRef.current?.contains(t)) return;
      if (menuBtnRef.current?.contains(t)) return;
      setMenuOpen(false);
    }
    function onEsc(e) { if (e.key === "Escape") setMenuOpen(false); }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [menuOpen]);
  useEffect(() => {
    try { localStorage.setItem(sortStorageKey, sortMode); }
    catch (_) { /* private-mode storage — ignore */ }
  }, [sortMode, sortStorageKey]);

  const sortedClients = useMemo(() => {
    const nameOf = (c) =>
      (c.business_name || c.contact_name || c.email || "").toLowerCase();
    const ts = (c) => (c.created_at ? new Date(c.created_at).getTime() : 0);
    const arr = [...clients];
    if (sortMode === "oldest") return arr.sort((a, b) => ts(a) - ts(b));
    if (sortMode === "newest") return arr.sort((a, b) => ts(b) - ts(a));
    return arr.sort((a, b) => nameOf(a).localeCompare(nameOf(b)));
  }, [clients, sortMode]);

  const SORT_LABELS = {
    alpha: "A → Z",
    oldest: "Oldest first",
    newest: "Newest first",
  };

  function handleDragOver(e) {
    if (e.dataTransfer.types.includes("application/x-migration-client")) {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      setDragOver(true);
    }
  }
  function handleDragLeave(e) {
    // Guard: dragleave also fires when the cursor enters a child element
    // (header, card, sort menu). ``relatedTarget`` is the incoming
    // element — if it's inside currentTarget we're still in the column.
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setDragOver(false);
  }
  function handleDrop(e) {
    e.preventDefault();
    setDragOver(false);
    const clientId = e.dataTransfer.getData("application/x-migration-client");
    if (clientId) onDropClient(clientId, stage.key);
  }
  return (
    <div
      className={`w-72 shrink-0 min-h-[calc(100vh-12rem)] rounded-lg transition-colors ${dragOver ? "bg-cyan-500/10 outline outline-2 outline-dashed outline-cyan-500/60 outline-offset-2" : ""}`}
      data-testid={`stage-column-${stage.key}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Sticky column header — pinned to the viewport as the PAGE
          scrolls. min-h on the column forces every column to at least
          viewport height so short/empty columns don't release the
          sticky header early. See MigrationBoard.jsx overflow-x:clip
          comment — do not add overflow-* to that scroll container. */}
      <div
        className="sticky top-16 z-20 flex items-center justify-between mb-3 px-2 py-2 bg-slate-950/95 backdrop-blur-sm rounded-md border border-slate-800/60 shadow-lg shadow-black/40"
      >
        <div className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full" style={{ background: stage.color || "#64748b" }} />
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-300">{stage.label}</div>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="relative">
            <button
              type="button"
              ref={menuBtnRef}
              onClick={() => setMenuOpen((v) => !v)}
              data-testid={`stage-sort-btn-${stage.key}`}
              title={`Sort: ${SORT_LABELS[sortMode]}`}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-widest text-slate-500 hover:text-cyan-300 hover:bg-slate-800/70 transition-colors"
            >
              {sortMode === "alpha" && <ArrowDownAZ className="w-3 h-3" />}
              {sortMode === "oldest" && <ArrowUp10 className="w-3 h-3" />}
              {sortMode === "newest" && <ArrowDown10 className="w-3 h-3" />}
            </button>
            {menuOpen && (
              <div
                ref={menuRef}
                data-testid={`stage-sort-menu-${stage.key}`}
                className="absolute right-0 top-full mt-1 z-30 w-40 rounded-lg border border-slate-700 bg-slate-900 shadow-xl shadow-black/50 py-1"
              >
                {["alpha", "oldest", "newest"].map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => { setSortMode(m); setMenuOpen(false); }}
                    data-testid={`stage-sort-option-${stage.key}-${m}`}
                    className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 hover:bg-slate-800 transition-colors ${
                      sortMode === m ? "text-cyan-300 font-semibold" : "text-slate-300"
                    }`}
                  >
                    {m === "alpha" && <ArrowDownAZ className="w-3 h-3" />}
                    {m === "oldest" && <ArrowUp10 className="w-3 h-3" />}
                    {m === "newest" && <ArrowDown10 className="w-3 h-3" />}
                    {SORT_LABELS[m]}
                    {sortMode === m && <CheckCircle2 className="w-3 h-3 ml-auto" />}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="text-xs text-slate-500 font-mono">{clients.length}</div>
        </div>
      </div>
      <div className="space-y-2 min-h-[120px] rounded-lg">
        {clients.length === 0 && (
          <div className="rounded-lg border border-dashed border-slate-800 bg-slate-900/40 p-3 text-xs text-slate-600 text-center">
            {dragOver ? "Drop here to move" : "No clients"}
          </div>
        )}
        {sortedClients.map(c => (
          <ClientCard key={c.id} client={c}
                      onOpen={onOpen}
                      isPartnerView={isPartnerView} syncroUrlBase={syncroUrlBase} />
        ))}
      </div>
    </div>
  );
}
